import { useState, useCallback } from 'react';
import type { Nullable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';

const COLOR_SCHEME_LOCAL_STORAGE_LOCATION = 'WISTIA_USER_COLOR_SCHEME_PREFERENCE';

export type LIGHT_SCHEME = 'light';
export type DARK_SCHEME = 'dark';
export type COLOR_SCHEME = DARK_SCHEME | LIGHT_SCHEME;
export type ChangeColorPreference = (newScheme: COLOR_SCHEME) => void;

export const useColorPreference = (): [COLOR_SCHEME, ChangeColorPreference] => {
  const [scheme, setScheme] = useState<COLOR_SCHEME>(() => {
    const storedPreference = localStorage.getItem(
      COLOR_SCHEME_LOCAL_STORAGE_LOCATION,
    ) as Nullable<COLOR_SCHEME>;
    const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');

    if (isNotNil(storedPreference)) {
      return storedPreference;
    }
    return prefersDarkScheme.matches ? 'dark' : 'light';
  });

  const changeColorPreference: ChangeColorPreference = useCallback(
    (newScheme: COLOR_SCHEME) => {
      setScheme(newScheme);
      localStorage.setItem(COLOR_SCHEME_LOCAL_STORAGE_LOCATION, newScheme);
    },
    [setScheme],
  );

  return [scheme, changeColorPreference];
};
